import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Candlestick patterns with educational feedback
const PATTERNS = [
  {
    id: 1,
    name: 'Bullish Engulfing',
    image: '📈',
    correctAnswer: 'buy',
    feedback: 'Strong reversal signal — Buy!',
    wrongFeedback: 'Bullish engulfing — Buy next time'
  },
  {
    id: 2,
    name: 'Bearish Engulfing',
    image: '📉',
    correctAnswer: 'sell',
    feedback: 'Reversal pattern — Sell correctly!',
    wrongFeedback: 'Bearish engulfing — Sell next time'
  },
  {
    id: 3,
    name: 'Support Bounce',
    image: '⬆️',
    correctAnswer: 'buy',
    feedback: 'Perfect buy at support!',
    wrongFeedback: 'Support bounce — Buy opportunity'
  },
  {
    id: 4,
    name: 'Resistance Rejection',
    image: '⬇️',
    correctAnswer: 'sell',
    feedback: 'Great sell at resistance!',
    wrongFeedback: 'Resistance rejection — Sell signal'
  },
  {
    id: 5,
    name: 'Uptrend',
    image: '📊',
    correctAnswer: 'buy',
    feedback: 'Riding the trend — Good buy!',
    wrongFeedback: 'Uptrend visible — Buy the dip'
  },
  {
    id: 6,
    name: 'Downtrend',
    image: '📉',
    correctAnswer: 'sell',
    feedback: 'Following the trend — Sell!',
    wrongFeedback: 'Downtrend active — Sell rally'
  },
  {
    id: 7,
    name: 'Support Break',
    image: '⚠️',
    correctAnswer: 'sell',
    feedback: 'Support broken — Sell!',
    wrongFeedback: 'Support break — Don\'t buy here'
  },
  {
    id: 8,
    name: 'Resistance Break',
    image: '🚀',
    correctAnswer: 'buy',
    feedback: 'Breakout — Buy signal!',
    wrongFeedback: 'Resistance broken — Buy breakout'
  },
  {
    id: 9,
    name: 'Hammer',
    image: '🔨',
    correctAnswer: 'buy',
    feedback: 'Hammer candlestick — Buy!',
    wrongFeedback: 'Hammer pattern — Bullish reversal'
  },
  {
    id: 10,
    name: 'Shooting Star',
    image: '⭐',
    correctAnswer: 'sell',
    feedback: 'Shooting star — Sell!',
    wrongFeedback: 'Shooting star — Bearish reversal'
  },
];

const LEVELS = [
  { name: 'Beginner', scoreNeeded: 0, color: 'text-green-500' },
  { name: 'Analyst', scoreNeeded: 15, color: 'text-blue-500' },
  { name: 'Pro Trader', scoreNeeded: 30, color: 'text-purple-500' },
  { name: 'Master', scoreNeeded: 50, color: 'text-yellow-500' },
];

function TradingGame() {
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [currentCell, setCurrentCell] = useState(null);
  const [board, setBoard] = useState(Array(9).fill(null));
  const [gameOver, setGameOver] = useState(false);
  const [timer, setTimer] = useState(5);
  const [feedback, setFeedback] = useState(null);
  const [level, setLevel] = useState(LEVELS[0]);

  useEffect(() => {
    // Update level based on total score
    const newLevel = [...LEVELS].reverse().find(l => totalScore >= l.scoreNeeded) || LEVELS[0];
    setLevel(newLevel);
  }, [totalScore]);

  useEffect(() => {
    if (currentCell !== null && timer > 0 && !feedback) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0 && !feedback) {
      handleTimeout();
    }
  }, [timer, currentCell, feedback]);

  const startNewRound = () => {
    const emptyCells = board.map((cell, idx) => cell === null ? idx : null).filter(idx => idx !== null);
    if (emptyCells.length === 0 || Math.abs(score) >= 5) {
      endGame();
      return;
    }
    const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const randomPattern = PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
    setCurrentCell({ index: randomCell, pattern: randomPattern });
    setTimer(5);
    setFeedback(null);
  };

  const handleChoice = (choice) => {
    if (!currentCell || feedback) return;

    const isCorrect = choice === currentCell.pattern.correctAnswer;
    const newBoard = [...board];
    newBoard[currentCell.index] = {
      choice,
      correct: isCorrect,
      pattern: currentCell.pattern
    };
    setBoard(newBoard);

    if (isCorrect) {
      setScore(score + 1);
      setTotalScore(totalScore + 1);
      setStreak(streak + 1);
      setFeedback({ type: 'success', message: currentCell.pattern.feedback });
    } else {
      setScore(score - 1);
      setStreak(0);
      setFeedback({ type: 'error', message: currentCell.pattern.wrongFeedback });
    }

    setTimeout(() => {
      startNewRound();
    }, 2000);
  };

  const handleTimeout = () => {
    const newBoard = [...board];
    newBoard[currentCell.index] = {
      choice: 'timeout',
      correct: false,
      pattern: currentCell.pattern
    };
    setBoard(newBoard);
    setScore(score - 1);
    setStreak(0);
    setFeedback({ type: 'error', message: '⏱️ Time\'s up! Think faster!' });

    setTimeout(() => {
      startNewRound();
    }, 2000);
  };

  const endGame = () => {
    setGameOver(true);
    setCurrentCell(null);
  };

  const resetGame = () => {
    setScore(0);
    setStreak(0);
    setBoard(Array(9).fill(null));
    setGameOver(false);
    setCurrentCell(null);
    setFeedback(null);
    startNewRound();
  };

  useEffect(() => {
    startNewRound();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20">
              <img
                src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                alt="John Ghatti"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xl font-light tracking-wide">JOHN GHATTI</span>
          </Link>
          <Link
            to="/"
            className="px-4 py-2 border border-zinc-700 text-sm hover:bg-zinc-800 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Game Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Game Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-light mb-4">🎮 Buy or Sell Board</h1>
            <p className="text-gray-400 font-light text-lg mb-6">
              Master forex trading patterns through gameplay
            </p>

            {/* Stats Bar */}
            <div className="flex justify-center gap-8 mb-8">
              <div className="text-center">
                <div className="text-3xl font-light mb-1">{score}</div>
                <div className="text-xs text-gray-500">ROUND SCORE</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-light mb-1">{totalScore}</div>
                <div className="text-xs text-gray-500">TOTAL SCORE</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-light mb-1">{streak}🔥</div>
                <div className="text-xs text-gray-500">STREAK</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-light mb-1 ${level.color}`}>{level.name}</div>
                <div className="text-xs text-gray-500">LEVEL</div>
              </div>
            </div>

            {/* Streak Bonus */}
            {streak >= 3 && (
              <div className="inline-block px-6 py-2 bg-yellow-500/20 border border-yellow-500 text-yellow-500 text-sm font-bold">
                🔥 SMART TRADER! {streak} STREAK BONUS
              </div>
            )}
          </div>

          {!gameOver ? (
            <>
              {/* Current Pattern Challenge */}
              {currentCell && (
                <div className="bg-zinc-950 border border-zinc-800 p-8 mb-8 text-center">
                  <div className="text-6xl mb-4">{currentCell.pattern.image}</div>
                  <h3 className="text-2xl font-light mb-2">{currentCell.pattern.name}</h3>
                  <div className="text-5xl font-bold text-yellow-500 mb-4">{timer}s</div>
                  
                  {!feedback && (
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => handleChoice('buy')}
                        className="px-12 py-6 bg-green-600 hover:bg-green-700 transition-colors text-2xl font-bold"
                      >
                        🟢 BUY
                      </button>
                      <button
                        onClick={() => handleChoice('sell')}
                        className="px-12 py-6 bg-red-600 hover:bg-red-700 transition-colors text-2xl font-bold"
                      >
                        🔴 SELL
                      </button>
                    </div>
                  )}

                  {feedback && (
                    <div className={`mt-4 p-4 border-2 ${feedback.type === 'success' ? 'border-green-500 bg-green-500/10 text-green-500' : 'border-red-500 bg-red-500/10 text-red-500'}`}>
                      <div className="text-xl font-bold mb-2">
                        {feedback.type === 'success' ? '✅ CORRECT!' : '❌ WRONG'}
                      </div>
                      <div className="text-sm">{feedback.message}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Game Board */}
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                {board.map((cell, index) => (
                  <div
                    key={index}
                    className={`aspect-square border-2 flex items-center justify-center text-4xl ${
                      cell === null
                        ? 'border-zinc-800 bg-zinc-950'
                        : cell.correct
                        ? 'border-green-500 bg-green-500/10'
                        : 'border-red-500 bg-red-500/10'
                    } ${currentCell?.index === index ? 'border-yellow-500 animate-pulse' : ''}`}
                  >
                    {cell && (
                      <div className="text-center">
                        <div className="text-3xl mb-1">{cell.pattern.image}</div>
                        <div className="text-xl">
                          {cell.choice === 'buy' ? '🟢' : cell.choice === 'sell' ? '🔴' : '⏱️'}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Progress */}
              <div className="mt-8 text-center">
                <div className="text-sm text-gray-400 mb-2">First to +5 or -5 ends the round</div>
                <div className="flex justify-center gap-2">
                  {[-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5].map((point) => (
                    <div
                      key={point}
                      className={`w-6 h-6 rounded-full border ${
                        score === point
                          ? 'bg-white border-white'
                          : point > 0
                          ? 'border-green-500'
                          : point < 0
                          ? 'border-red-500'
                          : 'border-zinc-700'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            /* Game Over Screen */
            <div className="bg-zinc-950 border border-zinc-800 p-12 text-center">
              <div className="text-6xl mb-4">
                {score > 0 ? '🎉' : '💪'}
              </div>
              <h2 className="text-4xl font-light mb-4">
                {score > 0 ? 'YOU WIN!' : 'KEEP LEARNING!'}
              </h2>
              <div className="text-6xl font-bold mb-6 text-yellow-500">{score > 0 ? '+' : ''}{score}</div>
              <p className="text-gray-400 mb-8">
                {score > 0
                  ? 'Great trading instincts! You\'re learning fast.'
                  : 'Every loss is a lesson. Try again and master the patterns!'}
              </p>
              
              <div className="space-y-4">
                <button
                  onClick={resetGame}
                  className="block w-full py-4 bg-white text-black text-sm font-bold tracking-widest hover:bg-gray-200 transition-colors"
                >
                  PLAY AGAIN
                </button>
                <Link
                  to="/"
                  className="block w-full py-3 border border-zinc-700 text-center text-sm font-light hover:bg-zinc-900 transition-colors"
                >
                  Back to Home
                </Link>
                <a
                  href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-4 bg-green-600 hover:bg-green-700 text-white text-center text-sm font-bold tracking-widest transition-colors"
                >
                  READY FOR REAL TRADING? GET KASANGO AI PRO →
                </a>
              </div>
            </div>
          )}

          {/* How to Play */}
          <div className="mt-12 bg-zinc-950 border border-zinc-800 p-6">
            <h3 className="text-xl font-light mb-4">📚 How to Play</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>• Each cell shows a forex trading pattern</li>
              <li>• You have 5 seconds to decide: BUY or SELL</li>
              <li>• Correct choice = +1 point | Wrong = -1 point</li>
              <li>• First to +5 points wins | -5 points ends the round</li>
              <li>• Build streaks for bonus recognition</li>
              <li>• Level up by earning more total points</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TradingGame;
